const env = process.env.VITE_ENV || (import.meta.env || {} || {}).VITE_ENV || 'test';

// Preview is a first-class named environment like test/demo/prod: a preview build
// bakes VITE_ENV=preview and VITE_PR_ID (scripts/deploy/netlify-deploy.mjs exports
// both; Vite inlines them). The prId feeds the config_preview block's dynamic
// constants below so the client talks to THIS PR's own Hasura instead of shared
// gql-test. test/prod builds (and the functions context, where import.meta.env is
// undefined) never carry either.
const prId = (import.meta.env || {}).VITE_PR_ID || process.env.VITE_PR_ID || null;
const config_preview = {
  graphqlHttpUrl: `https://gql-${prId}.pr.hopdrive.io/v1/graphql`,
  graphqlWsUrl: `wss://gql-${prId}.pr.hopdrive.io/v1/graphql`,
  graphqlClientName: `app-embed-preview-${prId}`,
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  twilioNumber: '+18045324554',
  // Base URL for the hoprides service. No trailing slash: callers append `/path`.
  // hoprides has no per-PR preview alias in this monorepo, so preview intentionally
  // stays pinned to the shared hoprides-test host.
  hopridesBaseUrl: 'https://hoprides-test.hopdrive.io',
};

// Local is a first-class named environment like test/preview/prod. It points at the
// local Hasura stack the hopdrive CLI's Caddy layer serves at gql.local.hopdrive.io,
// and at admin on its *.local.hopdrive.io domain (from admin's manifest.jsonc).
// Selected when VITE_ENV=local, which the `dev` script sets. Logging is on for
// developer visibility. twilioNumber / ridesSubdomain stay test: those siblings have
// no local target. A per-developer config.local.ts still overrides this as the last
// layer. See docs/local-dev.md.
const config_local = {
  graphqlHttpUrl: 'https://gql.local.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.local.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-embed-local',
  loggingEnableColoring: true,
  loggingEnabled: true,
  hopdriveAdminUrl: 'https://admin.local.hopdrive.io/',
  twilioNumber: '+18045324554',
  ridesSubdomain: 'hoprides-test',
};

const config_demo = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-embed-demo',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  twilioNumber: '+18045324554',
  hopridesBaseUrl: 'https://hoprides-test.hopdrive.io',
};

const config_test = {
  graphqlHttpUrl: 'https://gql-test.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql-test.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-embed-test',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin-test.hopdrive.io/',
  twilioNumber: '+18045324554',
  hopridesBaseUrl: 'https://hoprides-test.hopdrive.io',
};

const config_prod = {
  graphqlHttpUrl: 'https://gql.hopdrive.io/v1/graphql',
  graphqlWsUrl: 'wss://gql.hopdrive.io/v1/graphql',
  graphqlClientName: 'app-embed-prod',
  loggingEnableColoring: false,
  loggingEnabled: false,
  hopdriveAdminUrl: 'https://admin.hopdrive.io/',
  twilioNumber: '+18044092601',
  hopridesBaseUrl: 'https://hoprides.hopdrive.io',
};

const config = {
  appEnv: env,
  // Baked on preview builds only; the build-identity footer shows it as `preview <prId>`.
  prId,
  name: process.env.PKG_NAME || (import.meta.env || {}).PKG_NAME || 'portal-embed',
  version: process.env.PKG_VERSION || (import.meta.env || {}).PKG_VERSION || '3.0.0',
  fullName: `${process.env.PKG_NAME || (import.meta.env || {}).PKG_NAME || 'portal-embed'}-v${process.env.PKG_VERSION || (import.meta.env || {}).PKG_VERSION || '3.0.0'}`,
  versionCheckFrequency: 3600000,

  // Firebase claims
  firebaseClaimsNamespace: 'https://hasura.io/jwt/claims',
  firebaseClaimsAllowedRegions: 'x-hasura-allowed-regions',
  firebaseClaimsAllowedRoles: 'x-hasura-allowed-roles',
  firebaseClaimsDefaultRole: 'x-hasura-default-role',
  firebaseClaimsAllowedCustomers: 'x-hasura-allowed-customers',
  firebaseClaimsAllowedPayers: 'x-hasura-allowed-payers',
  firebaseClaimsPayerChildCustomers: 'x-hasura-payer-child-customers',
  firebaseClaimsCustomerId: 'x-hasura-customer-id',
  firebaseClaimsUserId: 'x-hasura-user-id',
  firebaseClaimsDriverId: 'x-hasura-driver-id',
  firebaseClaimsUserEmail: 'x-hasura-user-email',

  // Accessorial codes
  accessorialCodes: 'delay,fuel,partial,waived,surge,tow,toll,ride,insurance,reimbursement',

  // Quickbooks config
  quickbooksClientId: process.env.QB_CLIENT_ID || (import.meta.env || {}).QB_CLIENT_ID,
  quickbooksClientSecret: process.env.QB_CLIENT_SEC || (import.meta.env || {}).QB_CLIENT_SEC,
  quickbooksRedUrl: 'https://developer.intuit.com/v2/OAuth2Playground/RedirectUrl',
  quickbookCompanyId: process.env.QB_CMP_ID || (import.meta.env || {}).QB_CMP_ID,
  quickbooksDvrAct: '5010 Cost of Moves:Driver Cost',
  quickbooksEnvironment: process.env.QB_ENV || (import.meta.env || {}).QB_ENV,
  quickbooksApiUrl: process.env.QB_API_URL || (import.meta.env || {}).QB_API_URL,
  quickbooksProductId: process.env.QB_PRODUCT_ID || (import.meta.env || {}).QB_PRODUCT_ID,
  quickbooksFeeId: process.env.QB_FEE_ID || (import.meta.env || {}).QB_FEE_ID,
  quickbooksItemIds: process.env.QB_ITEM_IDS || (import.meta.env || {}).QB_ITEM_IDS,

  // Twilio config
  twilioSid: process.env.TWILIO_SID || (import.meta.env || {}).TWILIO_SID,
  twilioToken: process.env.TWILIO_TOK || (import.meta.env || {}).TWILIO_TOK,
  twilioTemplate: 'twilio_return_ride',

  google: {
    clientUrl: 'https://clients3.google.com/generate_204',
  },
  email: {
    info: 'info@hopdrive.com',
    support: 'support@hopdrive.com',
  },
  sentry: {
    url: 'https://5186cd19f98e4077a5c9ed9ebea39b8c@o491290.ingest.sentry.io/6751078',
  },
  logging: {
    enableColoring: true,
    enabled: true,
  },
  hopdrive: {
    admin: {
      url: 'https://admin-test.hopdrive.io/',
    },
  },
  ridehail: {
    enabled: true,
  },

  // Environment specific keys
  ...config_test, //opinionated default to test
  ...(env === 'local' ? config_local : null),
  ...(env === 'demo' ? config_demo : null),
  ...(env === 'test' ? config_test : null),
  // preview without a baked prId can't guess its PR; the test default above holds.
  ...(env === 'preview' && prId ? config_preview : null),
  ...(env === 'prod' || env === 'production' ? config_prod : null),
};

export default config;
